import { Cell } from "@ton/core";
import type { IndexedPoolEvent, ParsedPoolEvent, ReplayPosition } from "@tonresistor/zkresistor-sdk";
import {
  asHash,
  asRecord,
  asString,
  asTonAddress,
  parseProtocolManifest,
  parseRuntimeManifest,
  type CatalogResponse,
  type JettonMetadata,
  type JettonMetadataResponse,
  type JettonWalletResponse,
  type MerkleDeltaDocument,
  type PoolKind,
  type PoolViewResponse,
  type ProtocolApi,
  type ProtocolManifest,
  type RelayWithdrawalBinding,
  type RelayWithdrawalResponse,
  type RuntimeManifest,
} from "./contracts.js";
import { verifySignedEnvelope, type SignerPolicy } from "./signatures.js";

export class ProtocolApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ProtocolApiError";
  }
}

export interface ProtocolApiOptions {
  baseUrl: string;
  signer: SignerPolicy;
  protocolManifestSha256: string;
  fetch?: typeof globalThis.fetch;
  now?: () => number;
}

export const SIGNED_DOMAINS = {
  catalog: "zkresistor.catalog.v1",
  poolView: "zkresistor.pool-view.v1",
  jettonMetadata: "zkresistor.jetton-metadata.v1",
  jettonWallet: "zkresistor.jetton-wallet.v1",
  snapshot: "zkresistor.snapshot-manifest.v1",
} as const;

async function fetchPlainManifest<T>(url: string, parse: (value: unknown) => T): Promise<T> {
  const response = await fetch(url, { credentials: "omit", cache: "no-cache" });
  if (!response.ok) throw new ProtocolApiError(response.status, `${url} returned ${response.status}`);
  return parse(await response.json());
}

export function loadProtocolManifest(url = "/protocol-manifest.json"): Promise<ProtocolManifest> {
  return fetchPlainManifest(url, parseProtocolManifest);
}

export function loadRuntimeManifest(url = "/runtime-manifest.json"): Promise<RuntimeManifest> {
  return fetchPlainManifest(url, parseRuntimeManifest);
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

export function createProtocolApi(options: ProtocolApiOptions): ProtocolApi {
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const now = options.now ?? Date.now;
  const expectedProtocolManifestSha256 = asHash(
    options.protocolManifestSha256,
    "expected protocol manifest hash",
  );
  const highestMasterchainSeqno = new Map<string, number>();
  const maximumViewAgeMs = 120_000;

  function acceptFreshView<T extends { view: { masterchainSeqno: number; observedAt: string } }>(
    resource: string,
    value: T,
  ): T {
    const observed = Date.parse(value.view.observedAt);
    const currentTime = now();
    if (!Number.isFinite(observed) || currentTime - observed > maximumViewAgeMs || observed - currentTime > 30_000) {
      throw new Error("Gateway signed view is stale or has an invalid timestamp");
    }
    const previous = highestMasterchainSeqno.get(resource) ?? -1;
    if (value.view.masterchainSeqno < previous) {
      throw new Error("Gateway attempted to replay an older masterchain view");
    }
    highestMasterchainSeqno.set(resource, value.view.masterchainSeqno);
    return value;
  }

  async function signed<T>(
    path: string,
    domain: string,
    parse: (value: unknown) => T,
    init: RequestInit = {},
  ): Promise<T> {
    const response = await doFetch(joinUrl(options.baseUrl, path), {
      credentials: "omit",
      cache: "no-store",
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as {
        error?: string | { message?: unknown };
      } | null;
      throw new ProtocolApiError(
        response.status,
        typeof detail?.error === "string"
          ? detail.error
          : typeof detail?.error?.message === "string"
            ? detail.error.message
            : `Gateway returned ${response.status}`,
      );
    }
    return verifySignedEnvelope(await response.json(), options.signer, domain, parse);
  }

  async function plain<T>(
    path: string,
    parse: (value: unknown) => T,
    init: RequestInit = {},
  ): Promise<T> {
    const response = await doFetch(joinUrl(options.baseUrl, path), {
      credentials: "omit",
      cache: "no-store",
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as {
        error?: string | { message?: unknown };
      } | null;
      const message = typeof detail?.error === "string"
        ? detail.error
        : typeof detail?.error?.message === "string"
          ? detail.error.message
          : `Gateway returned ${response.status}`;
      throw new ProtocolApiError(response.status, message);
    }
    return parse(await response.json());
  }

  return {
    catalog(signal) {
      return signed(
        "catalog",
        SIGNED_DOMAINS.catalog,
        (value) => {
          const parsed = acceptFreshView("catalog", parseCatalog(value));
          if (parsed.protocolManifestSha256 !== expectedProtocolManifestSha256) {
            throw new Error("Catalog targets another protocol manifest");
          }
          return parsed;
        },
        signal === undefined ? {} : { signal },
      );
    },
    openPoolView(poolAddress, kind, signal) {
      const requestedPool = asTonAddress(poolAddress, "requested Pool");
      const query = new URLSearchParams({ kind });
      return signed(
        `pools/${encodeURIComponent(requestedPool)}/view?${query}`,
        SIGNED_DOMAINS.poolView,
        (value) => {
          const parsed = acceptFreshView(`pool:${requestedPool}`, parsePoolView(value));
          if (parsed.protocolManifestSha256 !== expectedProtocolManifestSha256) {
            throw new Error("Pool view targets another protocol manifest");
          }
          if (parsed.poolAddress !== requestedPool || parsed.kind !== kind) {
            throw new Error("Signed Pool view does not match the request");
          }
          return parsed;
        },
        signal === undefined ? {} : { signal },
      );
    },
    async jettonMetadata(jettonMaster, signal) {
      const requestedMaster = asTonAddress(jettonMaster, "requested Jetton master");
      const result = await signed(
        `jettons/${encodeURIComponent(requestedMaster)}/metadata`,
        SIGNED_DOMAINS.jettonMetadata,
        (value) => acceptFreshView(
          `jetton-metadata:${requestedMaster}`,
          parseJettonMetadataResponse(value),
        ),
        signal === undefined ? {} : { signal },
      );
      if (result.jettonMaster !== requestedMaster) {
        throw new Error("Signed Jetton metadata does not match the request");
      }
      if (result.protocolManifestSha256 !== expectedProtocolManifestSha256) {
        throw new Error("Jetton metadata targets another protocol manifest");
      }
      return result.metadata;
    },
    async jettonWalletAddress(jettonMaster, owner, signal) {
      const requestedMaster = asTonAddress(jettonMaster, "requested Jetton master");
      const requestedOwner = asTonAddress(owner, "requested Jetton owner");
      const result = await signed(
        `jettons/${encodeURIComponent(requestedMaster)}/wallet/${encodeURIComponent(requestedOwner)}`,
        SIGNED_DOMAINS.jettonWallet,
        (value) => acceptFreshView(
          `jetton:${requestedMaster}:${requestedOwner}`,
          parseWalletAddress(value),
        ),
        signal === undefined ? {} : { signal },
      );
      if (result.jettonMaster !== requestedMaster || result.owner !== requestedOwner) {
        throw new Error("Signed Jetton wallet response does not match the request");
      }
      if (result.protocolManifestSha256 !== expectedProtocolManifestSha256) {
        throw new Error("Jetton wallet response targets another protocol manifest");
      }
      return result.walletAddress;
    },
    async relayWithdrawal(request, signal) {
      const expected = parseRelayBinding(request);
      const result = await plain(
        "relay/withdraw",
        parseRelayWithdrawalResponse,
        {
          method: "POST",
          body: JSON.stringify({
            poolAddress: asTonAddress(request.poolAddress, "relay Pool"),
            payloadBoc: asBoc(request.payloadBoc, "withdraw payload"),
            ...(request.proof === undefined ? {} : { proof: request.proof }),
          }),
          ...(signal === undefined ? {} : { signal }),
        },
      );
      return assertRelayBinding(result, expected);
    },
    async relayWithdrawalStatus(jobId, expected, signal) {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(jobId)) {
        throw new TypeError("Invalid relay job id");
      }
      const result = await plain(
        `relay/${encodeURIComponent(jobId)}`,
        parseRelayWithdrawalResponse,
        signal === undefined ? {} : { signal },
      );
      if (result.jobId !== jobId) throw new Error("Relay status returned another job id");
      return assertRelayBinding(result, parseRelayBinding(expected));
    },
    async relayWithdrawalLookup(expected, signal) {
      const binding = parseRelayBinding(expected);
      const query = new URLSearchParams({
        poolAddress: binding.poolAddress,
        nullifierHash: binding.nullifierHash,
      });
      const result = await plain(
        `relay/lookup?${query.toString()}`,
        parseRelayWithdrawalResponse,
        signal === undefined ? {} : { signal },
      );
      return assertRelayBinding(result, binding);
    },
  };
}

function parseRelayBinding(value: RelayWithdrawalBinding): RelayWithdrawalBinding {
  return {
    poolAddress: asTonAddress(value.poolAddress, "relay Pool"),
    nullifierHash: asHash(value.nullifierHash, "relay nullifier hash"),
    recipient: asTonAddress(value.recipient, "relay recipient"),
  };
}

function assertRelayBinding(
  result: RelayWithdrawalResponse,
  expected: RelayWithdrawalBinding,
): RelayWithdrawalResponse {
  if (
    result.poolAddress !== expected.poolAddress ||
    result.nullifierHash !== expected.nullifierHash ||
    result.recipient !== expected.recipient
  ) throw new Error("Relay response does not match the locally generated withdrawal");
  return result;
}

function parseRelayWithdrawalResponse(value: unknown): RelayWithdrawalResponse {
  const record = asRecord(value, "relay withdrawal response");
  const status = asString(record.status, "relay status");
  if (status !== "validating" && status !== "submitted" && status !== "confirmed" && status !== "failed") {
    throw new TypeError("Invalid relay status");
  }
  const jobId = asString(record.jobId, "relay job id");
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(jobId)) {
    throw new TypeError("Invalid relay job id");
  }
  const nullifierHash = asHash(record.nullifierHash, "relay nullifier hash");
  const submittedAt = asString(record.submittedAt, "relay submittedAt");
  const updatedAt = asString(record.updatedAt, "relay updatedAt");
  if (!Number.isFinite(Date.parse(submittedAt)) || !Number.isFinite(Date.parse(updatedAt))) {
    throw new TypeError("Invalid relay timestamp");
  }
  return {
    jobId,
    status,
    poolAddress: asTonAddress(record.poolAddress, "relay Pool"),
    nullifierHash,
    recipient: asTonAddress(record.recipient, "relay recipient"),
    ...(record.transactionHash === undefined
      ? {}
      : { transactionHash: asHash(record.transactionHash, "relay transaction hash") }),
    submittedAt,
    updatedAt,
  };
}

function parseAccount(value: unknown) {
  const record = asRecord(value, "account state");
  const account: { status: string; data?: string; code?: string; balance?: string } = {
    status: asString(record.status, "account status"),
  };
  if (record.data !== undefined) account.data = asBoc(record.data, "account data");
  if (record.code !== undefined) account.code = asBoc(record.code, "account code");
  if (record.balance !== undefined) account.balance = asUintDecimal(record.balance, "account balance");
  return account;
}

function parseChainView(value: unknown) {
  const record = asRecord(value, "chain view");
  if (!Number.isSafeInteger(record.masterchainSeqno) || Number(record.masterchainSeqno) < 0) {
    throw new TypeError("masterchainSeqno must be a non-negative integer");
  }
  const observedAt = asString(record.observedAt, "observedAt");
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(observedAt)) {
    throw new TypeError("observedAt must be a canonical ISO timestamp");
  }
  const masterchainSeqno = Number(record.masterchainSeqno);
  const blockId = asString(record.blockId, "blockId");
  const blockMatch = /^-1:-?[0-9]+:([0-9]+):([0-9a-f]{64}):([0-9a-f]{64})$/.exec(blockId);
  if (blockMatch === null || Number(blockMatch[1]) !== masterchainSeqno) {
    throw new TypeError("blockId must be a canonical masterchain block identifier for masterchainSeqno");
  }
  return {
    masterchainSeqno,
    blockId,
    observedAt,
  };
}

function parsePoolKind(value: unknown): PoolKind {
  if (value !== "jetton" && value !== "ton") throw new TypeError("Invalid Pool kind");
  return value;
}

function parseCatalog(value: unknown): CatalogResponse {
  const record = asRecord(value, "catalog");
  const factory = asRecord(record.factory, "catalog Factory");
  if (
    record.schemaVersion !== 1 ||
    record.schema !== "zkresistor.catalog.v1" ||
    record.chain !== "mainnet" ||
    !Array.isArray(record.pools)
  ) {
    throw new TypeError("Invalid catalog response");
  }
  return {
    schemaVersion: 1,
    schema: "zkresistor.catalog.v1",
    protocolManifestSha256: asHash(record.protocolManifestSha256, "protocol manifest hash"),
    chain: "mainnet",
    view: parseChainView(record.view),
    factory: {
      address: asTonAddress(factory.address, "Factory address"),
      account: parseAccount(factory.account),
    },
    pools: record.pools.map((value) => {
      const pool = asRecord(value, "catalog Pool");
      return {
        address: asTonAddress(pool.address, "Pool address"),
        kind: parsePoolKind(pool.kind),
        jettonMaster: pool.jettonMaster === null
          ? null
          : asTonAddress(pool.jettonMaster, "Jetton master address"),
        privacySet: asSafeInteger(pool.privacySet, "Pool privacy set", 1_048_576),
        estimatedTvlNanoGram: pool.estimatedTvlNanoGram === undefined ||
          pool.estimatedTvlNanoGram === null
          ? null
          : asUintDecimal(pool.estimatedTvlNanoGram, "Pool estimated TVL"),
        account: parseAccount(pool.account),
        metadata: pool.metadata === null ? null : parseJettonMetadata(pool.metadata),
      };
    }),
  };
}

function parseCheckpoint(value: unknown) {
  const record = asRecord(value, "sync target");
  const position = asRecord(record.position, "checkpoint position");
  if (
    !Number.isSafeInteger(record.nextIndex) ||
    !Number.isSafeInteger(record.withdrawalCount) ||
    Number(record.nextIndex) < 0 ||
    Number(record.nextIndex) > 1_048_576 ||
    Number(record.withdrawalCount) < 0 ||
    Number(record.withdrawalCount) > Number(record.nextIndex)
  ) {
    throw new TypeError("Invalid sync target counters");
  }
  return {
    position: {
      blockSeqno: asSafeInteger(position.blockSeqno, "checkpoint blockSeqno", Number.MAX_SAFE_INTEGER),
      transactionLt: asUintDecimal(position.transactionLt, "checkpoint transaction LT", 0xffff_ffff_ffff_ffffn),
      eventIndex: asSafeInteger(position.eventIndex, "checkpoint eventIndex", 65_535),
    },
    nextIndex: Number(record.nextIndex),
    withdrawalCount: Number(record.withdrawalCount),
    currentRoot: asHash(record.currentRoot, "target currentRoot"),
    commitmentSeenRoots: parseRootTable(record.commitmentSeenRoots, "commitment roots"),
    nullifierSpentRoots: parseRootTable(record.nullifierSpentRoots, "nullifier roots"),
  };
}

function parseRootTable(value: unknown, name: string): string[] {
  if (!Array.isArray(value) || value.length !== 256) throw new TypeError(`${name} must contain 256 roots`);
  return value.map((root) => asHash(root, `${name} root`));
}

function parsePoolView(value: unknown): PoolViewResponse {
  const record = asRecord(value, "Pool view");
  const history = asRecord(record.history, "Pool history");
  if (record.schemaVersion !== 1 || record.schema !== "zkresistor.pool-view.v1") {
    throw new TypeError("Invalid Pool view schema");
  }
  const baseSnapshot = history.baseSnapshot === null
    ? null
    : parseBaseSnapshot(history.baseSnapshot);
  if (!Array.isArray(history.deltaChunks)) throw new TypeError("Pool history deltaChunks must be an array");
  const deltaChunks = parseDeltaChunkRefs(history.deltaChunks);
  return {
    schemaVersion: 1,
    schema: "zkresistor.pool-view.v1",
    protocolManifestSha256: asHash(record.protocolManifestSha256, "protocol manifest hash"),
    viewId: asString(record.viewId, "viewId"),
    view: parseChainView(record.view),
    poolAddress: asTonAddress(record.poolAddress, "Pool address"),
    kind: parsePoolKind(record.kind),
    checkpoint: parseCheckpoint(record.checkpoint),
    account: parseAccount(record.account),
    history: {
      baseSnapshot,
      deltaChunks,
    },
  };
}

function parseWalletAddress(value: unknown): JettonWalletResponse {
  const record = asRecord(value, "Jetton wallet response");
  if (record.schemaVersion !== 1 || record.schema !== "zkresistor.jetton-wallet.v1") {
    throw new TypeError("Invalid Jetton wallet response schema");
  }
  return {
    schemaVersion: 1,
    schema: "zkresistor.jetton-wallet.v1",
    view: parseChainView(record.view),
    protocolManifestSha256: asHash(record.protocolManifestSha256, "protocol manifest hash"),
    jettonMaster: asTonAddress(record.jettonMaster, "Jetton master"),
    owner: asTonAddress(record.owner, "Jetton wallet owner"),
    walletAddress: asTonAddress(record.walletAddress, "Jetton wallet address"),
  };
}

function parseJettonMetadataResponse(value: unknown): JettonMetadataResponse {
  const record = asRecord(value, "Jetton metadata response");
  if (record.schemaVersion !== 1 || record.schema !== "zkresistor.jetton-metadata.v1") {
    throw new TypeError("Invalid Jetton metadata response schema");
  }
  return {
    schemaVersion: 1,
    schema: "zkresistor.jetton-metadata.v1",
    view: parseChainView(record.view),
    protocolManifestSha256: asHash(record.protocolManifestSha256, "protocol manifest hash"),
    jettonMaster: asTonAddress(record.jettonMaster, "Jetton master"),
    metadata: parseJettonMetadata(record.metadata),
  };
}

function parseJettonMetadata(value: unknown): JettonMetadata {
  const record = asRecord(value, "Jetton metadata");
  if (!Number.isSafeInteger(record.decimals) || Number(record.decimals) < 0 || Number(record.decimals) > 30) {
    throw new TypeError("Jetton decimals are invalid");
  }
  if (typeof record.decimalsVerified !== "boolean") {
    throw new TypeError("Jetton decimals verification status is missing");
  }
  return {
    symbol: asDisplayText(record.symbol, "Jetton symbol", 64),
    decimals: Number(record.decimals),
    decimalsVerified: record.decimalsVerified,
    name: record.name === null ? null : asDisplayText(record.name, "Jetton name", 256),
    image: record.image === null ? null : safeTokenImagePath(record.image),
  };
}

function asDisplayText(value: unknown, name: string, maximumLength: number): string {
  const text = asString(value, name);
  if (
    text !== text.trim() ||
    text.length > maximumLength ||
    /[\u0000-\u001f\u007f]/.test(text)
  ) throw new TypeError(`${name} contains invalid display text`);
  return text;
}

function safeTokenImagePath(value: unknown): string | null {
  const path = asString(value, "Jetton image");
  if (path.length > 2_048 || path !== path.trim() || /[\u0000-\u001f\u007f]/.test(path)) {
    throw new TypeError("Jetton image metadata is invalid");
  }
  if (/^\/token-assets\/[0-9a-f]{2}\/[0-9a-f]{64}\.(?:jpg|png|webp)$/.test(path)) return path;
  try {
    const source = new URL(path);
    if (
      source.origin === "https://zk.resistance.dog" &&
      source.search === "" &&
      source.hash === "" &&
      /^\/token-assets\/[0-9a-f]{2}\/[0-9a-f]{64}\.(?:jpg|png|webp)$/.test(source.pathname)
    ) return source.pathname;
    if (
      source.protocol === "https:" &&
      source.username === "" &&
      source.password === "" &&
      source.hostname !== "" &&
      source.hash === "" &&
      source.href === path
    ) return null;
  } catch {}
  throw new TypeError("Jetton image must be HTTPS metadata or a content-addressed same-origin asset");
}

function asBoc(value: unknown, name: string): string {
  const boc = asString(value, name);
  try {
    Cell.fromBase64(boc);
  } catch (error) {
    throw new TypeError(`${name} must be a single-root base64 BOC`, { cause: error });
  }
  return boc;
}

function asUintDecimal(value: unknown, name: string, maximum?: bigint): string {
  const decimal = asString(value, name);
  if (!/^(?:0|[1-9][0-9]*)$/.test(decimal) || (maximum !== undefined && BigInt(decimal) > maximum)) {
    throw new TypeError(`${name} must be a canonical unsigned decimal`);
  }
  return decimal;
}

function asSameOriginAsset(value: unknown, name: string): string {
  const path = asString(value, name);
  if (!/^\/(?!\/)/.test(path) || path.includes("\\")) {
    throw new TypeError(`${name} must be a same-origin absolute path`);
  }
  return path;
}

function parseReplayPosition(value: unknown, name: string): ReplayPosition {
  const record = asRecord(value, name);
  return {
    blockSeqno: asSafeInteger(record.blockSeqno, `${name} blockSeqno`, Number.MAX_SAFE_INTEGER),
    transactionLt: BigInt(asUintDecimal(record.transactionLt, `${name} transaction LT`, 0xffff_ffff_ffff_ffffn)),
    eventIndex: asSafeInteger(record.eventIndex, `${name} eventIndex`, 65_535),
  };
}

function comparePosition(left: ReplayPosition, right: ReplayPosition): number {
  if (left.transactionLt !== right.transactionLt) return left.transactionLt < right.transactionLt ? -1 : 1;
  if (left.eventIndex !== right.eventIndex) return left.eventIndex - right.eventIndex;
  return left.blockSeqno - right.blockSeqno;
}

function parseContentAddressedRef(
  value: unknown,
  kind: "chunks" | "deltas",
  maximumBytes: number,
) {
  const record = asRecord(value, `${kind} reference`);
  const sha256 = asHash(record.sha256, `${kind} hash`);
  const extension = kind === "chunks" ? "bin" : "json";
  const url = asSameOriginAsset(record.url, `${kind} URL`);
  const expected = `/_zk_artifacts/${kind}/${sha256.slice(0, 2)}/${sha256}.${extension}`;
  if (url !== expected) throw new TypeError(`${kind} URL is not canonical content-addressed storage`);
  return {
    url,
    sha256,
    byteLength: asSafeInteger(record.byteLength, `${kind} byteLength`, maximumBytes),
  };
}

function parseDeltaChunkRefs(value: unknown[]) {
  if (value.length > 4_096) throw new TypeError("Pool view contains too many Merkle delta chunks");
  let totalBytes = 0;
  let totalEvents = 0;
  let previousScanned: ReplayPosition | null = null;
  const hashes = new Set<string>();
  return value.map((item, index) => {
    const record = asRecord(item, "delta chunk reference");
    const content = parseContentAddressedRef(record, "deltas", 16 * 1024 * 1024);
    const fromExclusive = parseReplayPosition(record.fromExclusive, "delta fromExclusive");
    const scannedThrough = parseReplayPosition(record.scannedThrough, "delta scannedThrough");
    const eventCount = asSafeInteger(record.eventCount, "delta eventCount", 1_048_576);
    if (comparePosition(fromExclusive, scannedThrough) >= 0) {
      throw new TypeError("Merkle delta range must advance");
    }
    if (previousScanned !== null && comparePosition(previousScanned, fromExclusive) !== 0) {
      throw new TypeError(`Merkle delta chunk ${index} is not contiguous`);
    }
    if (hashes.has(content.sha256)) throw new TypeError("Merkle delta chunk hash is duplicated");
    hashes.add(content.sha256);
    previousScanned = scannedThrough;
    totalBytes += content.byteLength;
    totalEvents += eventCount;
    if (totalBytes > 2 * 1024 * 1024 * 1024) throw new TypeError("Merkle delta bytes exceed the client limit");
    if (totalEvents > 1_048_576) throw new TypeError("Merkle delta events exceed tree capacity");
    return { ...content, fromExclusive, scannedThrough, eventCount };
  });
}

function parseSparseUpdate(value: unknown) {
  const record = asRecord(value, "sparse update");
  return {
    bucketId: asSafeInteger(record.bucketId, "sparse bucketId", 255),
    newRoot: BigInt(`0x${asHash(record.newRoot, "sparse newRoot")}`),
  };
}

function parseDeltaEvent(value: unknown): ParsedPoolEvent {
  const record = asRecord(value, "Merkle delta event");
  const kind = asString(record.kind, "Merkle delta event kind");
  if (kind === "deposit") {
    return {
      kind,
      leafIndex: asSafeInteger(record.leafIndex, "deposit leafIndex", 1_048_575),
      commitment: BigInt(`0x${asHash(record.commitment, "deposit commitment")}`),
      newRoot: BigInt(`0x${asHash(record.newRoot, "deposit newRoot")}`),
      fromUser: asTonAddress(record.fromUser, "deposit sender"),
      sparseUpdate: parseSparseUpdate(record.sparseUpdate),
    };
  }
  const common = {
    nullifierHash: BigInt(`0x${asHash(record.nullifierHash, "withdraw nullifier")}`),
    recipient: asTonAddress(record.recipient, "withdraw recipient"),
    payout: BigInt(asUintDecimal(record.payout, "withdraw payout")),
    sparseUpdate: parseSparseUpdate(record.sparseUpdate),
  };
  if (kind === "ton-withdraw") return { kind, ...common };
  if (kind === "jetton-withdraw") {
    return {
      kind,
      clientQueryId: BigInt(asUintDecimal(record.clientQueryId, "withdraw clientQueryId", 0xffff_ffff_ffff_ffffn)),
      ...common,
    };
  }
  throw new TypeError("Unknown Merkle delta event kind");
}

export function parseMerkleDelta(value: unknown): MerkleDeltaDocument {
  const record = asRecord(value, "Merkle delta");
  if (
    record.schemaVersion !== 1 ||
    record.schema !== "zkresistor.merkle-delta.v1" ||
    !Array.isArray(record.events)
  ) throw new TypeError("Invalid Merkle delta schema");
  if (record.events.length > 1_048_576) throw new TypeError("Merkle delta event limit exceeded");
  const fromExclusive = parseReplayPosition(record.fromExclusive, "delta fromExclusive");
  const scannedThrough = parseReplayPosition(record.scannedThrough, "delta scannedThrough");
  const checkpoint = asRecord(record.checkpoint, "delta checkpoint");
  const events: IndexedPoolEvent[] = record.events.map((item) => {
    const indexed = asRecord(item, "indexed Merkle event");
    return {
      position: parseReplayPosition(indexed.position, "Merkle event position"),
      event: parseDeltaEvent(indexed.event),
    };
  });
  let previous = fromExclusive;
  for (const indexed of events) {
    if (comparePosition(previous, indexed.position) >= 0 || comparePosition(indexed.position, scannedThrough) > 0) {
      throw new TypeError("Merkle delta event positions are not strictly increasing within the range");
    }
    previous = indexed.position;
  }
  return {
    schemaVersion: 1,
    schema: "zkresistor.merkle-delta.v1",
    poolAddress: asTonAddress(record.poolAddress, "Merkle delta Pool"),
    fromExclusive,
    scannedThrough,
    checkpoint: {
      nextIndex: asSafeInteger(checkpoint.nextIndex, "delta nextIndex", 1_048_576),
      withdrawalCount: asSafeInteger(checkpoint.withdrawalCount, "delta withdrawalCount", 1_048_576),
      currentRoot: BigInt(`0x${asHash(checkpoint.currentRoot, "delta currentRoot")}`),
    },
    events,
  };
}

function asSafeInteger(value: unknown, name: string, maximum: number): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0 || Number(value) > maximum || Object.is(value, -0)) {
    throw new TypeError(`${name} must be a canonical non-negative safe integer`);
  }
  return Number(value);
}

function parseBaseSnapshot(value: unknown): NonNullable<PoolViewResponse["history"]["baseSnapshot"]> {
  const record = asRecord(value, "base snapshot");
  if (!Array.isArray(record.chunks) || record.chunks.length === 0) {
    throw new TypeError("base snapshot must contain chunks");
  }
  if (record.chunks.length > 4_096) throw new TypeError("base snapshot contains too many chunks");
  let totalBytes = 0;
  const hashes = new Set<string>();
  const chunks = record.chunks.map((value) => {
    const chunk = parseContentAddressedRef(value, "chunks", 16 * 1024 * 1024);
    if (hashes.has(chunk.sha256)) throw new TypeError("snapshot chunk hash is duplicated");
    hashes.add(chunk.sha256);
    totalBytes += chunk.byteLength;
    if (totalBytes > 2 * 1024 * 1024 * 1024) throw new TypeError("snapshot bytes exceed the client limit");
    return chunk;
  });
  return {
    checkpoint: parseCheckpoint(record.checkpoint),
    chunks,
  };
}

export type { ProtocolApi, ProtocolManifest, RuntimeManifest };
